import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Loader2, Trash2, Search, Gift, DollarSign,
  X, Award, ChevronRight, CheckCircle2,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const STATUS_KEYS = ["pending", "contacted", "converted", "rewarded", "declined"];
const STATUS_COLORS = {
  pending: "bg-[#737373]", contacted: "bg-[#3B82F6]", converted: "bg-[#15803D]",
  rewarded: "bg-[#C4A252]", declined: "bg-[#8B1C22]",
};
const REWARD_COLORS = {
  pending: "text-[#C4A252] border-[#C4A252]",
  issued: "text-[#15803D] border-[#15803D]",
  paid_out: "text-[#0A0A0A] border-[#0A0A0A]",
  n_a: "text-[#A3A3A3] border-[#E5E5E5]",
};
const SOURCE_KEYS = ["word_of_mouth", "email", "text", "qr", "social", "other"];
const REWARD_TYPE_KEYS = ["discount_15", "discount_25", "credit_50", "credit_100", "free_notary", "free_consult", "custom"];

const StatCard = ({ label, value, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <div className={`mt-1 text-3xl font-serif-display ${accent}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const NewReferralModal = ({ clients, onClose, onSaved }) => {
  const t = useT();
  const [form, setForm] = useState({
    referrer_client_id: "", referrer_name: "", referrer_email: "",
    referred_name: "", referred_email: "", referred_phone: "",
    source: "word_of_mouth", reward_type: "", reward_amount: "", reward_note: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.referred_name.trim()) { toast.error(t("admin.referrals.toast.referred_required")); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.referrer_client_id) delete body.referrer_client_id;
      if (body.reward_amount === "") delete body.reward_amount;
      else body.reward_amount = parseFloat(body.reward_amount);
      if (!body.reward_type) delete body.reward_type;
      await api.post("/admin/referrals", body);
      toast.success(t("admin.referrals.toast.logged"));
      onSaved?.(); onClose();
    } catch { toast.error(t("admin.toast.failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="referral-new-modal">
      <div className="bg-white max-w-2xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div><div className="rn3-eyebrow"><Gift className="w-3 h-3 inline mr-1" /> {t("admin.nav.referrals")}</div><h3 className="font-serif-display text-2xl mt-1">{t("admin.referrals.new")}</h3></div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="rn3-eyebrow block mb-2">{t("admin.referrals.form.referrer_client")}</label>
            <select value={form.referrer_client_id} onChange={(e) => setForm({ ...form, referrer_client_id: e.target.value })} className="rn3-input w-full" data-testid="ref-form-referrer-client">
              <option value="">{t("admin.referrals.form.referrer_none")}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.email}</option>)}
            </select>
          </div>
          {!form.referrer_client_id && (
            <div className="grid grid-cols-2 gap-3">
              <input value={form.referrer_name} onChange={(e) => setForm({ ...form, referrer_name: e.target.value })} placeholder={t("admin.referrals.form.referrer_name")} className="rn3-input" data-testid="ref-form-referrer-name" />
              <input value={form.referrer_email} onChange={(e) => setForm({ ...form, referrer_email: e.target.value })} placeholder={t("admin.referrals.form.referrer_email")} className="rn3-input" data-testid="ref-form-referrer-email" />
            </div>
          )}
          <div className="pt-3 border-t border-[#F3EFE9]">
            <label className="rn3-eyebrow block mb-2">{t("admin.referrals.form.referred_party")}</label>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.referred_name} onChange={(e) => setForm({ ...form, referred_name: e.target.value })} placeholder={t("admin.referrals.form.referred_name")} className="rn3-input" data-testid="ref-form-referred-name" />
              <input value={form.referred_email} onChange={(e) => setForm({ ...form, referred_email: e.target.value })} placeholder={t("common.email")} className="rn3-input" data-testid="ref-form-referred-email" />
              <input value={form.referred_phone} onChange={(e) => setForm({ ...form, referred_phone: e.target.value })} placeholder={t("common.phone")} className="rn3-input" data-testid="ref-form-referred-phone" />
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rn3-input" data-testid="ref-form-source">
                {SOURCE_KEYS.map((k) => <option key={k} value={k}>{t(`admin.referrals.source.${k}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="pt-3 border-t border-[#F3EFE9]">
            <label className="rn3-eyebrow block mb-2">{t("admin.referrals.form.reward")}</label>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.reward_type} onChange={(e) => setForm({ ...form, reward_type: e.target.value })} className="rn3-input" data-testid="ref-form-reward-type">
                <option value="">{t("admin.referrals.form.no_reward")}</option>
                {REWARD_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(`admin.referrals.reward_type.${k}`)}</option>)}
              </select>
              <input type="number" step="0.01" value={form.reward_amount} onChange={(e) => setForm({ ...form, reward_amount: e.target.value })} placeholder={t("admin.referrals.form.reward_amount")} className="rn3-input" data-testid="ref-form-reward-amount" />
            </div>
            <input value={form.reward_note} onChange={(e) => setForm({ ...form, reward_note: e.target.value })} placeholder={t("admin.referrals.form.reward_note")} className="rn3-input w-full mt-3" data-testid="ref-form-reward-note" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{t("admin.referrals.form.notes")}</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input w-full" data-testid="ref-form-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E5E5]">
            <button onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] px-3">{t("common.cancel")}</button>
            <button onClick={save} disabled={busy} className="rn3-btn-primary" data-testid="ref-form-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("admin.referrals.log")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReferralsTab = () => {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (q) params.q = q;
      const [r, s, c] = await Promise.all([
        api.get("/admin/referrals", { params }),
        api.get("/admin/referrals/stats"),
        api.get("/admin/clients"),
      ]);
      setItems(r.data); setStats(s.data); setClients(c.data);
    } catch { toast.error(t("admin.referrals.toast.load_failed")); }
    finally { setLoading(false); }
  }, [statusFilter, q, t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/referrals/${id}`, { status }); load(); }
    catch { toast.error(t("admin.toast.failed")); }
  };
  const issueReward = async (id) => {
    try { await api.post(`/admin/referrals/${id}/issue-reward`); toast.success(t("admin.referrals.toast.reward_issued")); load(); }
    catch { toast.error(t("admin.toast.failed")); }
  };
  const markPaidOut = async (id) => {
    try { await api.patch(`/admin/referrals/${id}`, { reward_status: "paid_out" }); toast.success(t("admin.referrals.toast.paid_out")); load(); }
    catch { toast.error(t("admin.toast.failed")); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("admin.referrals.confirm_delete"))) return;
    try { await api.delete(`/admin/referrals/${id}`); load(); toast.success(t("admin.referrals.toast.deleted")); }
    catch { toast.error(t("admin.toast.failed")); }
  };

  return (
    <div className="rn3-container py-10" data-testid="referrals-tab">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="referrals-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.back_to_admin")}
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow"><Gift className="w-3 h-3 inline mr-1" /> {t("admin.referrals.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-2">{t("admin.referrals.heading")}</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="rn3-btn-primary" data-testid="ref-new-btn">
          <Plus className="w-4 h-4" /> {t("admin.referrals.new")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
        <StatCard label={t("admin.referrals.stat.total")} value={stats?.total ?? "—"} testid="stat-ref-total" />
        <StatCard label={t("admin.referrals.status.pending")} value={stats?.pending ?? "—"} testid="stat-ref-pending" />
        <StatCard label={t("admin.referrals.status.converted")} value={(stats?.converted ?? 0) + (stats?.rewarded ?? 0)} accent="text-[#15803D]" testid="stat-ref-converted" />
        <StatCard label={t("admin.referrals.stat.rewards_pending")} value={stats?.rewards_pending ?? "—"} accent={stats?.rewards_pending > 0 ? "text-[#C4A252]" : ""} testid="stat-ref-rewards-pending" />
        <StatCard label={t("admin.referrals.stat.rewards_owed")} value={`$${(stats?.pending_reward_value ?? 0).toLocaleString()}`} accent={stats?.pending_reward_value > 0 ? "text-[#8B1C22]" : ""} testid="stat-ref-value" />
      </div>

      {stats?.top_referrers?.length > 0 && (
        <div className="mt-6 border border-[#E5E5E5] bg-[#FDFBF7] p-4" data-testid="ref-top-referrers">
          <div className="rn3-eyebrow mb-3 flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#C4A252]" /> {t("admin.referrals.top_referrers")}</div>
          <div className="grid md:grid-cols-5 gap-3">
            {stats.top_referrers.map((r) => (
              <div key={r.client_id} className="text-sm" data-testid={`top-referrer-${r.client_id}`}>
                <div className="font-medium truncate">{r.name || r.email}</div>
                <div className="text-xs text-[#737373]">{r.total} {t("admin.referrals.referrals_lower")} · {r.converted} {t("admin.referrals.converted_lower")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mt-8 border-t border-[#E5E5E5] pt-6">
        <div className="flex-1 min-w-[240px]">
          <label className="rn3-eyebrow block mb-2">{t("common.search")}</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.referrals.search_placeholder")} className="rn3-input w-full pl-9" data-testid="ref-search" />
          </div>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.col.status")}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rn3-input" data-testid="ref-filter-status">
            <option value="all">{t("admin.referrals.all_statuses")}</option>
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{t(`admin.referrals.status.${k}`)}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" /></div>}
      {!loading && (
        <div className="overflow-x-auto mt-6">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr>
                <th className="py-3 pr-4">{t("admin.referrals.col.referrer_referred")}</th>
                <th className="py-3 pr-4">{t("admin.referrals.col.source")}</th>
                <th className="py-3 pr-4">{t("admin.col.status")}</th>
                <th className="py-3 pr-4">{t("admin.referrals.col.reward")}</th>
                <th className="py-3 pr-4">{t("admin.referrals.col.created")}</th>
                <th className="py-3 pr-4 text-right">{t("admin.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const rewardColor = REWARD_COLORS[r.reward_status] || REWARD_COLORS.n_a;
                const rewardLabel = r.reward_type ? t(`admin.referrals.reward_type.${r.reward_type}`) : null;
                const rewardStatusLabel = r.reward_status && r.reward_status !== "n_a" ? t(`admin.referrals.reward_status.${r.reward_status}`) : "—";
                return (
                  <tr key={r.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`ref-row-${r.id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium inline-flex items-center gap-1.5">
                        <span className="text-[#737373]">{r.referrer_snapshot?.name || r.referrer_snapshot?.email || t("admin.referrals.unknown")}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#C4A252]" />
                        <span className="text-[#0A0A0A]">{r.referred_name}</span>
                      </div>
                      {r.referred_email && <div className="text-xs text-[#737373]">{r.referred_email}{r.referred_phone ? ` · ${r.referred_phone}` : ""}</div>}
                    </td>
                    <td className="py-3 pr-4 text-xs capitalize text-[#737373]">{r.source ? t(`admin.referrals.source.${r.source}`) : "—"}</td>
                    <td className="py-3 pr-4">
                      <select
                        value={r.status}
                        onChange={(e) => setStatus(r.id, e.target.value)}
                        className="text-[10px] uppercase tracking-widest border border-[#E5E5E5] bg-white px-1.5 py-0.5"
                        data-testid={`ref-status-select-${r.id}`}
                      >
                        {STATUS_KEYS.map((k) => <option key={k} value={k}>{t(`admin.referrals.status.${k}`)}</option>)}
                      </select>
                      <span className={`ml-2 inline-block w-2 h-2 ${STATUS_COLORS[r.status] || STATUS_COLORS.pending} rounded-full`} aria-hidden />
                    </td>
                    <td className="py-3 pr-4 text-xs">
                      {rewardLabel ? (
                        <>
                          <div className="font-medium">{rewardLabel}{r.reward_amount ? ` · $${r.reward_amount}` : ""}</div>
                          <span className={`inline-block mt-0.5 px-1.5 py-0.5 text-[10px] uppercase tracking-widest border ${rewardColor}`}>{rewardStatusLabel}</span>
                        </>
                      ) : <span className="text-[#A3A3A3]">—</span>}
                    </td>
                    <td className="py-3 pr-4 text-xs text-[#737373]">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-4 text-right whitespace-nowrap">
                      {r.reward_status === "pending" && r.reward_type && (
                        <button onClick={() => issueReward(r.id)} className="text-[10px] uppercase tracking-widest text-[#15803D] hover:text-[#0F5D2C] mr-2" data-testid={`ref-issue-${r.id}`}>
                          <CheckCircle2 className="w-3 h-3 inline" /> {t("admin.referrals.issue")}
                        </button>
                      )}
                      {r.reward_status === "issued" && (
                        <button onClick={() => markPaidOut(r.id)} className="text-[10px] uppercase tracking-widest text-[#0A0A0A] hover:text-[#8B1C22] mr-2" data-testid={`ref-paidout-${r.id}`}>
                          <DollarSign className="w-3 h-3 inline" /> {t("admin.referrals.mark_paid")}
                        </button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`ref-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-[#737373]">{t("admin.referrals.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewReferralModal clients={clients} onClose={() => setShowNew(false)} onSaved={load} />}
    </div>
  );
};

export default ReferralsTab;
